(() => {
  if (customElements.get('tt-product-gallery')) return;
  customElements.define('tt-product-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.track = this.querySelector('[data-tt-gallery-track]');
      this.slides = [...this.querySelectorAll('[data-tt-slide]')];
      this.index = 0;
      this.onClick = e => {
        if (e.target.closest('[data-tt-gallery-prev]')) this.go(this.index - 1);
        if (e.target.closest('[data-tt-gallery-next]')) this.go(this.index + 1);
        const dot = e.target.closest('[data-tt-gallery-dot]');
        if (dot) this.go(Number(dot.dataset.ttGalleryDot));
      };
      this.onKey = e => {
        if (e.target !== this.track) return;
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index + (e.key === 'ArrowRight' ? 1 : -1)); }
        if (e.key === 'Home' || e.key === 'End') { e.preventDefault(); this.go(e.key === 'Home' ? 0 : this.slides.length - 1); }
      };
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          if (this.track.clientWidth) this.index = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / this.track.clientWidth)));
          this.updateControls();
        });
      };
      this.addEventListener('click', this.onClick);
      this.track.addEventListener('keydown', this.onKey);
      this.track.addEventListener('scroll', this.onScroll, {passive:true});
      if (typeof ResizeObserver !== 'undefined') {
        this.resize = new ResizeObserver(() => this.track.scrollTo({left:this.index * this.track.clientWidth, behavior:'instant'}));
        this.resize.observe(this.track);
      }
      this.updateControls();
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.track?.removeEventListener('keydown', this.onKey);
      this.track?.removeEventListener('scroll', this.onScroll);
      this.resize?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null; this.ready = false;
    }
    go(index) {
      this.index = Math.max(0, Math.min(this.slides.length - 1, index));
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({left:this.index * this.track.clientWidth, behavior:reduced ? 'instant' : 'smooth'});
      this.updateControls();
    }
    goToImage(id) {
      if (this.dataset.custom === 'true' || !id) return;
      const index = this.slides.findIndex(slide => slide.dataset.imageId === id);
      if (index >= 0) this.go(index);
    }
    updateControls() {
      const prev = this.querySelector('[data-tt-gallery-prev]'), next = this.querySelector('[data-tt-gallery-next]'), counter = this.querySelector('[data-tt-gallery-counter]');
      if (prev) prev.disabled = this.index <= 0;
      if (next) next.disabled = this.index >= this.slides.length - 1;
      if (counter) counter.textContent = `${this.index + 1} / ${this.slides.length}`;
      for (const dot of this.querySelectorAll('[data-tt-gallery-dot]')) {
        if (Number(dot.dataset.ttGalleryDot) === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      }
    }
  });
})();

(() => {
  if (customElements.get('treezyn-tuesday')) return;
  class TreezynTuesday extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.mount = this.querySelector('[data-tt-mount]');
      this.deals = [...this.querySelectorAll('template[data-tt-deal]')];
      this.preview = '';
      this.format = new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: this.dataset.currency || this.dataset.baseCurrency });
      this.querySelector('[data-tt-preview]')?.addEventListener('change', e => { this.preview = e.target.value; this.active = null; this.tick(); });
      this.addEventListener('change', e => { if (e.target.matches('[data-tt-variant],[data-tt-quantity]')) this.updateProducts(); });
      this.addEventListener('submit', e => {
        if (!e.target.matches('[data-type="add-to-cart-form"]')) return;
        if (!this.isLive(this.active) || this.preview) { e.preventDefault(); e.stopImmediatePropagation(); this.tick(); }
      }, true);
      this.addEventListener('click', e => { if (e.target.closest('[data-tt-bundle]')) this.addBundle(e.target.closest('[data-tt-bundle]')); });
      this.visibilityHandler = () => this.tick();
      document.addEventListener('visibilitychange', this.visibilityHandler);
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); document.removeEventListener('visibilitychange', this.visibilityHandler); this.ready = false; }
    dates(deal) {
      if (!deal) return null;
      const d = deal.dataset;
      const iso = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:Z|[+-]\d{2}:\d{2})$/;
      if (!iso.test(d.start) || !iso.test(d.end)) return null;
      const start = Date.parse(d.start), end = Date.parse(d.end);
      return Number.isFinite(start) && Number.isFinite(end) && end > start ? { start, end } : null;
    }
    configured(deal) {
      if (!deal || !this.dates(deal)) return false;
      const d = deal.dataset, value = Number(d.value);
      if (d.enabled !== 'true' || d.verified !== 'true' || !d.discount.trim() || !d.key.trim()) return false;
      if (d.currency !== this.dataset.currency || d.currency !== this.dataset.baseCurrency) return false;
      if (!Number.isFinite(value) || value < 0 || (d.type === 'percent' && (value <= 0 || value > 100))) return false;
      const cards = deal.content.querySelectorAll('[data-tt-product]');
      if (!cards.length || (d.type === 'bxgy' && cards.length !== 2)) return false;
      return ['percent', 'fixed', 'exact', 'bxgy'].includes(d.type);
    }
    isLive(deal) {
      if (!this.configured(deal)) return false;
      const {start, end} = this.dates(deal), now = Date.now();
      return now >= start && now < end;
    }
    choose() {
      if (this.dataset.editor === 'true' && this.preview) return this.deals.find(d => d.dataset.key === this.preview) || null;
      return this.deals.find(d => (!this.dataset.manual || d.dataset.key === this.dataset.manual) && this.isLive(d)) || null;
    }
    tick() {
      const next = this.choose();
      if (next !== this.active || !this.mount.childNodes.length) {
        this.active = next;
        this.mount.replaceChildren((next || this.querySelector('template[data-tt-idle]')).content.cloneNode(true));
        this.querySelector('[data-tt-extras]').hidden = !next;
        for (const [selector, flag] of [['[data-tt-benefits]', 'benefits'], ['[data-tt-details]', 'details'], ['[data-tt-teaser]', 'teaser'], ['[data-tt-faq]', 'faq']]) {
          const el = this.querySelector(selector);
          if (el) el.hidden = next ? next.dataset[flag] !== 'true' : flag !== 'faq';
        }
        for (const el of this.querySelectorAll('[data-deal-key]')) el.hidden = !!el.dataset.dealKey && el.dataset.dealKey !== next?.dataset.key;
        const firstImage = this.mount.querySelector('.tt-card__image');
        if (firstImage && !this.mount.querySelector('.tt-hero__image')) { firstImage.loading = 'eager'; firstImage.fetchPriority = 'high'; }
        this.updateProducts();
      }
      const time = this.mount.querySelector('[data-tt-countdown]');
      if (time) {
        const dates = this.dates(this.active);
        if (!dates) { time.textContent = '—'; return; }
        let seconds = Math.max(0, Math.floor((dates.end - Date.now()) / 1000));
        const days = Math.floor(seconds / 86400); seconds %= 86400;
        const hours = Math.floor(seconds / 3600), minutes = Math.floor((seconds % 3600) / 60), remaining = seconds % 60;
        time.textContent = `${days ? days + 'd ' : ''}${String(hours).padStart(2,'0')} : ${String(minutes).padStart(2,'0')} : ${String(remaining).padStart(2,'0')}`;
        const label = this.mount.querySelector('[data-tt-end-label]');
        label.textContent = new Intl.DateTimeFormat(this.dataset.locale || 'en', { timeZone: 'America/New_York', weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }).format(new Date(dates.end - 1000));
      }
    }
    price(regular, role) {
      const d = this.active.dataset, amount = Math.round(Number(d.value) * 100);
      if (d.type === 'percent') return Math.max(0, Math.round(regular * (1 - Number(d.value) / 100)));
      if (d.type === 'fixed') return Math.max(0, regular - amount);
      if (d.type === 'exact' || (d.type === 'bxgy' && role === 'y')) return Math.min(regular, amount);
      return regular;
    }
    updateProducts() {
      if (!this.active) return;
      const previewing = !!this.preview && this.dataset.editor === 'true';
      for (const card of this.mount.querySelectorAll('[data-tt-product]')) {
        const select = card.querySelector('[data-tt-variant]'), option = select.selectedOptions[0];
        if (!option) continue;
        const regular = Number(option.dataset.price), promotional = this.price(regular, card.dataset.role);
        const compareAt = Number(option.dataset.comparePrice);
        const comparison = Number.isFinite(compareAt) && compareAt > regular ? compareAt : regular;
        const gallery = card.querySelector('tt-product-gallery');
        if (gallery && gallery.dataset.selectedVariant !== select.value) {
          gallery.goToImage?.(option.dataset.imageId);
          gallery.dataset.selectedVariant = select.value;
        }
        const quantity = card.querySelector('[data-tt-quantity]');
        quantity.min = option.dataset.min || '1'; quantity.step = option.dataset.step || '1';
        if (option.dataset.max) quantity.max = option.dataset.max; else quantity.removeAttribute('max');
        if (Number(quantity.value) < Number(quantity.min)) quantity.value = quantity.min;
        const discounted = comparison > promotional;
        card.querySelector('[data-tt-price]').textContent = this.format.format(promotional / 100);
        const before = card.querySelector('[data-tt-regular]'); before.hidden = !discounted; before.textContent = this.format.format(comparison / 100);
        const comparisonRow = card.querySelector('[data-tt-comparison]'); if (comparisonRow) comparisonRow.hidden = !discounted;
        const savings = card.querySelector('[data-tt-savings]'); savings.hidden = !discounted; savings.textContent = `${this.dataset.saveLabel} ${this.format.format((comparison - promotional) / 100)} (${comparison ? Math.round((comparison-promotional)/comparison*100) : 0}%)`;
        const button = card.querySelector('[data-tt-add]');
        button.disabled = previewing || option.dataset.available !== 'true';
        button.querySelector('span').textContent = option.dataset.available === 'true' ? this.dataset.addLabel : this.dataset.soldLabel;
      }
      const bundle = this.mount.querySelector('[data-tt-bundle]');
      if (bundle) bundle.disabled = previewing || !this.bundleItems();
    }
    bundleItems() {
      const cards = [...this.mount.querySelectorAll('[data-tt-product]')];
      if (cards.length !== 2) return null;
      const items = cards.map(card => {
        const select = card.querySelector('[data-tt-variant]'), option = select.selectedOptions[0], input = card.querySelector('[data-tt-quantity]');
        if (!option || option.dataset.available !== 'true' || !input.checkValidity()) return null;
        const quantity = Number(input.value);
        return Number.isInteger(quantity) && quantity > 0 ? {id: select.value, quantity} : null;
      });
      return items.every(Boolean) && items[0].quantity === items[1].quantity ? items : null;
    }
    async addBundle(button) {
      if (this.preview || !this.isLive(this.active)) { this.tick(); return; }
      const items = this.bundleItems(); if (!items) return;
      const error = this.mount.querySelector('[data-tt-bundle-error]'); error.textContent = '';
      button.disabled = true; button.setAttribute('aria-busy', 'true');
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const root = window.Shopify?.routes?.root || '/';
      let deferred;
      try {
        const Event = window.StandardEvents?.CartLinesUpdateEvent;
        if (Event) {
          deferred = Event.createPromise();
          this.dispatchEvent(new Event({action:'add', context:'product', lines:items.map(i=>({merchandiseId:i.id, quantity:i.quantity})), promise:deferred.promise}));
        }
        cart?.setActiveElement?.(button);
        const body = {items, sections_url: location.pathname};
        if (cart?.getSectionsToRender) body.sections = cart.getSectionsToRender().map(s=>s.id);
        const response = await fetch(`${root}cart/add.js`, {method:'POST', headers:{'Content-Type':'application/json', Accept:'application/json', 'X-Requested-With':'XMLHttpRequest'}, body:JSON.stringify(body)});
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || this.dataset.errorLabel);
        const current = await fetch(`${root}cart.js`).then(r=>r.ok?r.json():null).catch(()=>null);
        if (deferred) {
          if (current) deferred.resolve({cart:Event.createCartFromAjaxResponse(current)});
          else deferred.reject(new Error('Cart refresh failed'));
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, {source:'treezyn-tuesday', cartData:current || result});
        if (cart?.renderContents && result.sections) {
          if (cart.tagName.toLowerCase() === 'cart-notification') result.key = result.items?.[0]?.key;
          cart.classList.remove('is-empty'); cart.renderContents(result);
        } else location.assign(`${root}cart`);
      } catch (e) {
        error.textContent = e.message || this.dataset.errorLabel;
        deferred?.reject(e);
        const Event = window.StandardEvents?.CartErrorEvent;
        if (Event) this.dispatchEvent(new Event({error:error.textContent, code:'INVALID'}));
      } finally {
        button.removeAttribute('aria-busy');
        this.tick(); this.updateProducts();
      }
    }
  }
  customElements.define('treezyn-tuesday', TreezynTuesday);
})();
